import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

const darkField = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

export default function AddressEditDialog({ trip, field, open, onOpenChange, onSaved }) {
  const [value, setValue] = useState("");
  const [check, setCheck] = useState(null);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");
  const key = field === "pickup" ? "pickup_address" : "dropoff_address";
  const label = field === "pickup" ? "Pickup" : "Drop-off";

  useEffect(() => {
    if (open && trip) { setValue(trip[key] || ""); setCheck(null); setOverride(false); setReason(""); }
  }, [open, trip, key]);

  const save = async (addr, verification) => {
    setBusy("save");
    try {
      await fleetApi.post(`/trips/${trip.id}/address-fix`, { field, action: "edit", address: addr });
      await api.post("/addresses/manual-edit", { trip_id: trip.id, field, old: trip[key] || null, new: addr, verification, reason: reason || null })
        .catch(() => toast.warning("Address saved, but the change log couldn't be written."));
      toast.success(verification === "verified" ? `${label} address verified and saved.` : `${label} address saved as UNVERIFIED — map routing/ETA may be unavailable until it's confirmed.`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(fleetError(e, "Couldn't update the address.")); }
    finally { setBusy(""); }
  };

  const checkAndSave = async () => {
    const addr = value.trim();
    if (addr.length < 5) { toast.error("Enter the full address."); return; }
    if (check && check.addr === addr && check.status !== "valid") {
      if (!override) { toast.error("Tick “Save as unverified” or use the suggestion."); return; }
      save(addr, "manual_unverified");
      return;
    }
    setBusy("check");
    try {
      const { data } = await api.post("/addresses/check-one", { address: addr });
      if (data.status === "valid") { setBusy(""); save(addr, "verified"); return; }
      setCheck({ ...data, addr });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => (b === "check" ? "" : b)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white sm:max-w-lg" data-testid="address-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {label.toLowerCase()} address</DialogTitle>
          <DialogDescription className="text-slate-400">We check it with Google first. Changes only this trip, reach the driver right away, and are logged with your name.</DialogDescription>
        </DialogHeader>
        <input value={value} onChange={(e) => { setValue(e.target.value); setCheck(null); setOverride(false); }} className={darkField} data-testid="address-edit-input" />
        {check && (
          <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-100" data-testid="address-edit-check">
            <p className="flex items-center gap-1.5 font-semibold text-amber-300"><AlertTriangle className="h-3.5 w-3.5" />
              {check.status === "unchecked" ? "Couldn't verify right now" : check.status === "invalid" ? "Google couldn't find this address" : "This address looks off"}
            </p>
            {check.reason && <p>{check.reason}</p>}
            {check.suggestion && check.suggestion !== check.addr && (
              <button onClick={() => { setValue(check.suggestion); setCheck(null); }} data-testid="address-edit-use-suggestion"
                className="flex items-center gap-1.5 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-left text-blue-300 hover:bg-slate-800">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Use: {check.suggestion}
              </button>
            )}
            <label className="flex cursor-pointer items-start gap-2 pt-1">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-amber-500" data-testid="address-edit-override" />
              <span>Save as <b>unverified</b> anyway. It will be marked as entered by hand, and maps/ETA may not work until it's confirmed.</span>
            </label>
            {override && (
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional), e.g. new building not in Google yet"
                className={darkField} data-testid="address-edit-reason" />
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="address-edit-cancel">Cancel</Button>
          <Button onClick={checkAndSave} disabled={!!busy || (check && !override && check.addr === value.trim())} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="address-edit-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {check && override ? "Save unverified" : "Check & save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
